import type { Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import type { SearchEntity } from "src/queries/search/search.types";
import {
  CUSTOM_LANGUAGE_ID,
  ENTITY_HIGHLIGHT_CLASS,
  ENTITY_TRIGGER_CHAR,
} from "../ChatInput.constants";
import type { MonacoEditor, MonacoModel, MonacoPosition } from "../ChatInput.types";
import type { CompletionProvider } from "./skill.utils";

export interface EntityDecorationsSetup {
  updateDecorations: () => void;
  decorationType: ReturnType<MonacoEditor["createDecorationsCollection"]>;
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  pipeline: "Pipeline",
  task: "Task",
  dataset: "Dataset",
  compute: "Compute",
};

export function setupEntityDecorations(
  editor: MonacoEditor,
  monaco: Monaco,
  entitiesRef: React.RefObject<SearchEntity[]>
): EntityDecorationsSetup {
  const decorationType = editor.createDecorationsCollection();

  const updateDecorations = () => {
    const model = editor.getModel();
    if (!model) return;

    const text = model.getValue();
    const entities = entitiesRef.current || [];

    if (!entities.length) {
      decorationType.set([]);
      return;
    }

    // Build a set of known entity names for quick lookup
    const entityNames = new Set(entities.map((e) => e.name));

    // Match @<name> patterns where name is a known entity
    const decorations: editor.IModelDeltaDecoration[] = [];
    const mentionPattern = /@(\S+)/g;

    for (const match of text.matchAll(mentionPattern)) {
      const mentionName = match[1];
      if (!entityNames.has(mentionName)) continue;

      const startPos = model.getPositionAt(match.index!);
      const endPos = model.getPositionAt(match.index! + match[0].length);
      decorations.push({
        range: new monaco.Range(
          startPos.lineNumber,
          startPos.column,
          endPos.lineNumber,
          endPos.column
        ),
        options: {
          inlineClassName: ENTITY_HIGHLIGHT_CLASS,
        },
      });
    }

    decorationType.set(decorations);
  };

  return { updateDecorations, decorationType };
}

export function createEntityCompletionProvider(
  monaco: Monaco,
  entitiesRef: React.RefObject<SearchEntity[]>
): CompletionProvider {
  const completionDisposable = monaco.languages.registerCompletionItemProvider(
    CUSTOM_LANGUAGE_ID,
    {
      triggerCharacters: [ENTITY_TRIGGER_CHAR],
      provideCompletionItems: (model: MonacoModel, position: MonacoPosition) => {
        const textUntilPosition = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });

        // Find the @ trigger - could be "@" or "@partial"
        const mentionMatch = textUntilPosition.match(/@(\S*)$/);
        if (!mentionMatch) {
          return { suggestions: [] };
        }

        const filterText = mentionMatch[1].toLowerCase();
        const triggerStart = position.column - mentionMatch[0].length;

        const range = {
          startLineNumber: position.lineNumber,
          startColumn: triggerStart,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        };

        const entities = entitiesRef.current || [];
        if (!entities.length) return { suggestions: [] };

        // Filter entities by the partial text after @
        const filtered = filterText
          ? entities.filter((e) => e.name.toLowerCase().includes(filterText))
          : entities;

        return {
          suggestions: filtered.map((entity, index) => ({
            label: `@${entity.name}`,
            kind: monaco.languages.CompletionItemKind.Reference,
            insertText: `@${entity.name} `,
            range,
            detail: ENTITY_TYPE_LABELS[entity.type] ?? entity.type,
            sortText: String(index).padStart(6, "0"),
            filterText: `@${entity.name}`,
          })),
        };
      },
    }
  );

  return { dispose: () => completionDisposable.dispose() };
}
